﻿import { debounce } from '@fe/core';
import {
    CheckboxChangeEvent,
    ColorPickerChangeEvent,
    NumericTextBoxChangeEvent,
    RadioGroupChangeEvent,
    SliderChangeEvent,
    SwitchChangeEvent,
    TextBoxChangeEvent
} from '@progress/kendo-react-inputs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SettingKeys, SettingsStringKeys, SettingValue } from '../../../../services/settings-service';
import { DropdownList, DropdownListWithValueFieldChangeEvent } from '../../../components/inputs/DropdownListWithValueField';
import { RootState } from '../../../state/slices';
import { setSetting } from '../../../state/slices/settings-slice';
import { RadioButtonList } from './forms';

function createSettingHook<T, E>(handler: (e: E) => T) {
    return <K extends SettingKeys>(key: K, options?: { debounceMs?: number; uppercase?: boolean }) => {
        const dispatch = useDispatch();
        const currentValue = useSelector((state: RootState) => state.settings[key] as SettingValue<K>);
        const [value, setValue] = useState(currentValue);

        useEffect(() => {
            setValue(currentValue);
        }, [currentValue]);

        const dispatchStateChange = useMemo(
            () =>
                debounce((value: SettingValue<K>) => {
                    dispatch(setSetting(key, value));
                }, options?.debounceMs ?? 500),
            [key, dispatch, options]
        );

        const onChange = useCallback(
            (e: E) => {
                const newValue = handler(e) as SettingValue<K>;
                dispatchStateChange(newValue);
                setValue(newValue);
            },
            [key, dispatch, dispatchStateChange]
        );

        return { value, onChange };
    };
}

function createSettingHookChecked<T, E>(handler: (e: E) => T) {
    const hook = createSettingHook<T, E>(handler);
    return <K extends SettingKeys>(key: K) => {
        const result = hook(key);
        return {
            checked: result.value,
            onChange: result.onChange
        };
    };
}

function settingsTextBoxHook<K extends SettingsStringKeys>(key: K, options?: { debounceMs?: number; uppercase?: boolean }) {
    const dispatch = useDispatch();
    const currentValue = useSelector((state: RootState) => state.settings[key]) as SettingValue<K>;
    const [value, setValue] = useState(currentValue);

    useEffect(() => {
        setValue(currentValue);
    }, [currentValue]);

    const dispatchStateChange = useMemo(
        () =>
            debounce((value: string) => {
                dispatch(setSetting(key, value));
            }, options?.debounceMs ?? 500),
        [key, dispatch, options?.debounceMs]
    );

    const onChange = useCallback(
        (e: TextBoxChangeEvent) => {
            let v = e.value as string;
            if (options?.uppercase) {
                v = v.toUpperCase();
            }
            dispatchStateChange(v);
            setValue(v);
        },
        [key, dispatch, dispatchStateChange, options]
    );
    return { value: value ?? '', onChange };
}

export const useSettingCheckboxValue = createSettingHook<boolean, CheckboxChangeEvent>((e) => e.value);
export const useSettingStringValue = settingsTextBoxHook;
export const useSettingSwitchValue = createSettingHookChecked<boolean, SwitchChangeEvent>((e) => e.value);
export const useSettingNumericValue = createSettingHook<number | null, NumericTextBoxChangeEvent>((e) => e.value);
export const useSettingSliderValue = createSettingHook<number | undefined, SliderChangeEvent>((e) => e.value);
export const useSettingColorValue = createSettingHook<string, ColorPickerChangeEvent>((e) => e.value as string);

export const useSettingRadioValue = <K extends SettingKeys>(data: RadioButtonList<SettingValue<K>>, key: K) => {
    const dispatch = useDispatch();
    const currentValue = useSelector((state: RootState) => state.settings[key]) as SettingValue<K>;
    const [value, setValue] = useState<SettingValue<K>>(currentValue);

    useEffect(() => {
        setValue(currentValue);
    }, [currentValue]);

    const onChange = useCallback(
        (e: RadioGroupChangeEvent) => {
            const newValue = e.value as SettingValue<K>;
            setValue(newValue);

            dispatch(setSetting(key, newValue));
        },
        [key, dispatch]
    );

    return {
        value,
        data,
        onChange
    };
};

export const useSettingDropdownValue = <K extends SettingKeys>(data: DropdownList<SettingValue<K>>, key: K) => {
    const dispatch = useDispatch();
    const currentValue = useSelector((state: RootState) => state.settings[key]) as SettingValue<K>;
    const [value, setValue] = useState<SettingValue<K>>(currentValue);

    useEffect(() => {
        setValue(currentValue);
    }, [currentValue]);

    const onChange = useCallback(
        (e: DropdownListWithValueFieldChangeEvent<SettingValue<K>>) => {
            const newValue = e.value;
            setValue(newValue);

            dispatch(setSetting(key, newValue));
        },
        [key, dispatch]
    );

    return {
        value,
        data,
        onChange
    };
};
